import { css } from '@emotion/react';
import {
  TutorialHighlightElementCardAnatomy,
  TutorialTurnData,
} from '@mythos/game/Tutorial';
import invariant from 'invariant';
import { useEffect } from 'react';
import ActionStore from './ActionStore';
import CardView, { CardContainerStyles } from './CardView';
import { ColorPalette, UIColorPalette } from './ColorPalette';
import GameButton, { GameButtonTheme } from './GameButton';
import HoverCardStore from './HoverCardStore';
import TextWithSymbols from './TextWithSymbols';
import TributeCardView from './TributeCardView';

export function TutorialTooltip(props: { tutorialData: TutorialTurnData }) {
  return <div>TutorialTooltip</div>;
}

export const TUTORIAL_HIGHLIGHT_COLOR = '#FFD700'; // gold
export const CardSectionHighlightStyle = css({
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    boxShadow: `0 0 10px 3px ${TUTORIAL_HIGHLIGHT_COLOR}`,
    borderRadius: 2,
    zIndex: 1,
  },
});
export const PlayerSummarySectionHighlightStyle = css({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    boxShadow: `0 0 10px 3px #0da62c`,
    borderRadius: 2,
    zIndex: 1,
    animation: 'tutorial-highlight-pulse 0.5s infinite alternate',
    '@keyframes tutorial-highlight-pulse': {
      '0%': {
        boxShadow: `0 0 10px 3px #0da62c`,
      },
      '100%': {
        boxShadow: `0 0 20px 8px #0da62c`,
      },
    },
  },
  // TODO add a pointing arrow
});

export function TutorialMessage(props: { actionStore: ActionStore }) {
  const tutorialCard = props.actionStore.getTutorialHighlightCard();
  const tutorialTribute = props.actionStore.getTutorialHighlightTribute();
  useEffect(() => {
    if (tutorialCard) {
      const containerStyles: CardContainerStyles = {};
      for (const highlight of tutorialCard.highlights) {
        switch (highlight) {
          case TutorialHighlightElementCardAnatomy.CARD_NAME:
            containerStyles.title = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_COST:
            containerStyles.cost = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_OBTAIN:
            containerStyles.obtain = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_TYPE:
            containerStyles.type = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_AFFINITY:
            containerStyles.affinity = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_EFFECTS:
            containerStyles.effect = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_FAVOR:
            containerStyles.baseFavor = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.CARD_PROPHECY:
            containerStyles.prophecy = CardSectionHighlightStyle;
            break;
          case TutorialHighlightElementCardAnatomy.ENTIRE_CARD:
            containerStyles.frame = CardSectionHighlightStyle;
            break;
          default:
            invariant(false, 'unknown highlight: %s', highlight);
        }
      }
      HoverCardStore.setTutorialCard(tutorialCard.card, (data) => {
        return <CardView card={data.card} containerStyles={containerStyles} />;
      });
      HoverCardStore.setPosition('bottom-right');
    } else if (tutorialTribute) {
      HoverCardStore.setTutorialCard(
        tutorialTribute.tribute,
        (data) => {
          return <TributeCardView card={data.card} />;
        },
        'tutorial-top',
      );
      HoverCardStore.setPosition('bottom-right');
    } else {
      HoverCardStore.clearTutorialCard();
    }
  }, [tutorialCard, tutorialTribute]);

  const message = props.actionStore.getTutorialMessage();
  if (!message) {
    return null;
  }
  const messageText = message.text;

  return (
    <div
      css={[
        TutorialStyles.messageContainer,
        TutorialPositionStyles[message.location ?? 'top'],
      ]}
    >
      <GameButton
        theme={GameButtonTheme.TutorialNext}
        css={TutorialStyles.nextButton}
        onClick={() => props.actionStore.didClickTutorialBack()}
        disabled={!props.actionStore.canClickTutorialBack()}
      >
        Back
      </GameButton>
      <TextWithSymbols
        css={TutorialStyles.messageText}
        content={messageText}
        symbolStyle={TutorialStyles.symbol}
      />
      <GameButton
        theme={GameButtonTheme.TutorialNext}
        css={TutorialStyles.nextButton}
        onClick={() => props.actionStore.didClickTutorialNext()}
        disabled={!props.actionStore.isTutorialNextButtonEnabled()}
      >
        Next
      </GameButton>
    </div>
  );
}
const TutorialStyles = {
  messageContainer: css({
    position: 'fixed',
    left: 150,

    border: `5px solid ${UIColorPalette.playerBoardBG}`,

    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,

    width: 600,
    height: 100,

    backgroundColor: ColorPalette.lightBG,
    color: ColorPalette.normalText,
    borderRadius: 5,
    padding: 10,

    lineHeight: 1.25,

    boxShadow: `0px 0px 25px 15px ${UIColorPalette.tutorialButtonShadow}`,
  }),
  messageText: css({
    textAlign: 'center',
    flexGrow: 1,
  }),
  nextButton: css({
    height: '100%',
  }),
  symbol: css({
    height: 14,
    width: 'auto',
    verticalAlign: 'text-bottom',
    marginBottom: 3,
  }),
};
const TutorialPositionStyles = {
  top: css({
    top: 10,
  }),
  bottom: css({
    bottom: 180,
  }),
};
